import { useState, useEffect, useRef, useCallback } from 'react'

const BREAKPOINT = 1024
const DEBOUNCE_MS = 100

/**
 * useResponsiveTable - Hook for breakpoint detection and responsive table layout.
 *
 * Uses matchMedia for initial check and ResizeObserver with debounce
 * to track container/window width changes.
 *
 * @returns {{ isCompact: boolean }} - true when viewport <= 1024px
 */
const useResponsiveTable = () => {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth <= BREAKPOINT
  })
  const timerRef = useRef(null)

  const handleChange = useCallback((matches) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setIsCompact(matches)
    }, DEBOUNCE_MS)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const mql = window.matchMedia(`(max-width: ${BREAKPOINT}px)`)

    setIsCompact(mql.matches)

    const listener = (e) => handleChange(e.matches)

    if (mql.addEventListener) {
      mql.addEventListener('change', listener)
    } else {
      mql.addListener(listener)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (mql.removeEventListener) {
        mql.removeEventListener('change', listener)
      } else {
        mql.removeListener(listener)
      }
    }
  }, [handleChange])

  return { isCompact }
}

export default useResponsiveTable
